import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  CircularProgress,
  TablePagination,
  Tooltip,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import RefreshIcon from '@material-ui/icons/Refresh';
import DirectionsBusIcon from '@material-ui/icons/DirectionsBus';
import Alert from '@material-ui/lab/Alert';
import { useNavigate } from 'react-router-dom';
import { firestore } from '../../firebase/config';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';

import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import StatusChip from '../../components/ui/StatusChip';
import SearchBar from '../../components/ui/SearchBar';

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  search: { maxWidth: 300, flexGrow: 1 },
  count: { color: theme.palette.text.secondary, fontSize: '0.85rem' },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 280,
  },
  occupancyBar: {
    height: 5,
    borderRadius: 99,
    background: '#e2e8f0',
    overflow: 'hidden',
    minWidth: 80,
  },
  occupancyFill: {
    height: '100%',
    borderRadius: 99,
    background: 'linear-gradient(90deg, #10b981, #6366f1)',
  },
}));

const formatDate = (val) => {
  if (!val) return '—';
  const d = val.toDate ? val.toDate() : new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatPrice = (p) => {
  if (p == null) return '—';
  return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0 }).format(p) + ' FCFA';
};

const STATUS_NORM = {
  Scheduled: 'scheduled',
  Completed: 'completed',
  Cancelled: 'cancelled',
  InProgress: 'in_progress',
  scheduled: 'scheduled',
  completed: 'completed',
  cancelled: 'cancelled',
  in_progress: 'in_progress',
};

const STATUS_LABEL = {
  scheduled: 'Programmé',
  completed: 'Terminé',
  cancelled: 'Annulé',
  in_progress: 'En cours',
};

const TripsListPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();

  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [tripToDelete, setTripToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchTrips = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(firestore, 'trips'));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setTrips(list);
    } catch (err) {
      console.error('Error fetching trips:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la récupération des voyages', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrips();
  }, []);

  const handleDeleteClick = (trip) => {
    setTripToDelete(trip);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!tripToDelete) return;
    try {
      await deleteDoc(doc(firestore, 'trips', tripToDelete.id));
      setTrips((prev) => prev.filter((t) => t.id !== tripToDelete.id));
      setSnackbar({ open: true, message: 'Voyage supprimé avec succès', severity: 'success' });
    } catch (err) {
      console.error('Error deleting trip:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la suppression', severity: 'error' });
    } finally {
      setOpenDeleteDialog(false);
      setTripToDelete(null);
    }
  };

  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return trips.filter((t) => {
      const matchSearch =
        (t.routeName || '').toLowerCase().includes(q) ||
        (t.origin || '').toLowerCase().includes(q) ||
        (t.destination || '').toLowerCase().includes(q) ||
        (t.vehicleName || '').toLowerCase().includes(q) ||
        (t.driverName || '').toLowerCase().includes(q);
      const norm = STATUS_NORM[t.status] || 'scheduled';
      const matchStatus = statusFilter === 'all' || norm === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [trips, searchTerm, statusFilter]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box className="fade-in-up">
      <PageHeader
        breadcrumbs={[{ label: 'Admin', to: '/admin' }, { label: 'Voyages' }]}
        title="Voyages"
        subtitle={`${filtered.length} voyage${filtered.length > 1 ? 's' : ''}`}
        actions={
          <>
            <Tooltip title="Rafraîchir">
              <IconButton
                onClick={fetchTrips}
                style={{ background: '#fff', border: '1px solid rgba(17,24,39,0.08)' }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/admin/trips/create')}
            >
              Nouveau voyage
            </Button>
          </>
        }
      />

      <Paper>
        <Box className={classes.toolbar}>
          <Box className={classes.search}>
            <SearchBar
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0); }}
              placeholder="Rechercher par itinéraire, véhicule, chauffeur…"
            />
          </Box>

          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={statusFilter}
              label="Statut"
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">Tous les statuts</MenuItem>
              <MenuItem value="scheduled">Programmé</MenuItem>
              <MenuItem value="in_progress">En cours</MenuItem>
              <MenuItem value="completed">Terminé</MenuItem>
              <MenuItem value="cancelled">Annulé</MenuItem>
            </Select>
          </FormControl>

          <Typography className={classes.count}>
            {filtered.length} résultat{filtered.length > 1 ? 's' : ''}
          </Typography>
        </Box>

        {loading ? (
          <Box className={classes.loading}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<DirectionsBusIcon style={{ fontSize: 36 }} />}
            title="Aucun voyage"
            description={
              searchTerm || statusFilter !== 'all'
                ? 'Aucun voyage ne correspond à vos filtres.'
                : 'Commencez par créer un premier voyage.'
            }
            actionLabel={!searchTerm && statusFilter === 'all' ? 'Créer un voyage' : undefined}
            onAction={() => navigate('/admin/trips/create')}
          />
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Itinéraire</TableCell>
                    <TableCell>Départ</TableCell>
                    <TableCell>Arrivée</TableCell>
                    <TableCell>Véhicule / Chauffeur</TableCell>
                    <TableCell>Occupation</TableCell>
                    <TableCell>Prix</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((t) => {
                    const norm = STATUS_NORM[t.status] || 'scheduled';
                    const total = (t.totalSeats || 0) || ((t.bookedSeats || 0) + (t.availableSeats || 0));
                    const booked = t.bookedSeats || 0;
                    const pct = total > 0 ? Math.round((booked / total) * 100) : 0;

                    return (
                      <TableRow key={t.id} hover>
                        <TableCell>
                          <Box style={{ fontWeight: 600 }}>{t.routeName || '—'}</Box>
                          {t.origin && t.destination && (
                            <Box style={{ fontSize: '0.75rem', color: '#64748b' }}>
                              {t.origin} → {t.destination}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell style={{ whiteSpace: 'nowrap' }}>
                          {formatDate(t.departureDate)}
                        </TableCell>
                        <TableCell style={{ whiteSpace: 'nowrap' }}>
                          {formatDate(t.arrivalDate)}
                        </TableCell>
                        <TableCell>
                          <Box style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                            {t.vehicleName || '—'}
                          </Box>
                          <Box style={{ fontSize: '0.75rem', color: '#64748b' }}>
                            {t.driverName || '—'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box style={{ fontSize: '0.8rem', marginBottom: 4, color: '#64748b' }}>
                            {booked}/{total} ({pct}%)
                          </Box>
                          <Box className={classes.occupancyBar}>
                            <Box
                              className={classes.occupancyFill}
                              style={{ width: `${pct}%` }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell style={{ whiteSpace: 'nowrap' }}>
                          {formatPrice(t.price)}
                        </TableCell>
                        <TableCell>
                          <StatusChip
                            status={norm}
                            label={STATUS_LABEL[norm] || t.status}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Voir">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/admin/trips/${t.id}`)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {norm !== 'completed' && (
                            <Tooltip title="Modifier">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/admin/trips/${t.id}/edit`)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Supprimer">
                            <IconButton
                              size="small"
                              style={{ color: '#ef4444' }}
                              onClick={() => handleDeleteClick(t)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Par page"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
            />
          </>
        )}
      </Paper>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Supprimer le voyage{' '}
            <strong>{tripToDelete?.routeName}</strong>{' '}
            du {tripToDelete ? formatDate(tripToDelete.departureDate) : ''} ?
            Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleDeleteConfirm}
            style={{ background: '#ef4444', color: '#fff' }}
            autoFocus
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TripsListPage;
